// Marketing pages, new design (the homepage, /business, /api, /genre-charts, /radar/early-finds, /contact, legal pages behind the
// New design flag): their own Vite entry. No Alpine, no Livewire: from app.js only the consent-gated PostHog
// (tracking-consent.js), plus a few small behaviours in plain JS:
// - [data-mk-filter]: chip buttons that show only the rows whose data-genres contain the chosen key, with an empty
//   state when none match;
// - [data-mk-tabs]: tabs that swap panels (the API page's code samples);
// - [data-mk-toc]: "On this page" built from the article's h2s, following the scroll;
// - [data-mk-copy]: copy a value (the support email) and say so.
// - [data-hh]: the homepage hero (the first line is shown, the second types itself, an arrow curves from each onto
//   its carousel of song cards, the carousel grows in; the carousels turn by drag, swipe or arrow keys; on a phone a
//   switch shows one at a time);
// - [data-hm-hero]: the homepage story (card-to-dot wires, the curve's tooltip; switching when there are tabs);
// - [data-hm-bu]: the homepage's About to blow up cards (Alpine `hmCards`, the card's own actions without the list
//   around it) and their phone swipe row's dots;
// - [data-hm-fx]: today's chart window growing into place as it scrolls in;
// - [data-hm-br]: the browser window around today's chart (loads the chart inside once it nears the screen; back,
//   forward and reload for it, and its address and "Open the full page" following it).
import './alpine/ui-tip';
import './alpine/ui-rows';
import './alpine/ui-undo';
import './alpine/ui-drop';
import './tracking-consent';

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

/* ---------- genre filter ---------- */
$$('[data-mk-filter]').forEach((bar) => {
    const target = document.getElementById(bar.dataset.mkFilter);
    if (!target) return;
    const rows = $$('[data-genres]', target);
    const scope = target.closest('section') ?? target.parentElement;
    const empty = $('[data-mk-empty]', scope);
    const shown = $('[data-mk-shown]', scope);
    const more = $(`[data-mk-more="${target.id}"]`);
    let expanded = false;
    more?.addEventListener('click', () => {
        expanded = true;
        more.parentElement.hidden = true;
        $('button[aria-pressed="true"]', bar)?.click();
    });
    bar.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-key]');
        if (!btn) return;
        $$('button[data-key]', bar).forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        const key = btn.dataset.key;
        let n = 0;
        rows.forEach((row) => {
            // The first 20 show until "Show all"; a genre shows every match.
            const on = key === 'all' ? (expanded || !row.hasAttribute('data-mk-extra')) : row.dataset.genres.split(' ').includes(key);
            row.hidden = !on;
            if (on) n++;
        });
        if (empty) empty.hidden = n > 0;
        if (shown) shown.textContent = String(n);
        if (more && !expanded) more.parentElement.hidden = key !== 'all';
    });
});

/* ---------- tabs ---------- */
$$('[data-mk-tabs]').forEach((list) => {
    const tabs = $$('[role="tab"]', list);
    const select = (tab) => {
        tabs.forEach((t) => {
            const on = t === tab;
            t.setAttribute('aria-selected', String(on));
            t.tabIndex = on ? 0 : -1;
            const panel = document.getElementById(t.getAttribute('aria-controls'));
            if (panel) panel.hidden = !on;
        });
    };
    list.addEventListener('click', (e) => { const t = e.target.closest('[role="tab"]'); if (t) select(t); });
    list.addEventListener('keydown', (e) => {
        const i = tabs.indexOf(document.activeElement);
        if (i < 0 || !['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
        const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        next.focus();
        select(next);
    });
});

/* ---------- On this page ---------- */
const slug = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
$$('[data-mk-toc]').forEach((nav) => {
    const article = document.getElementById(nav.dataset.mkToc);
    if (!article) return;
    const heads = $$('h2', article);
    if (heads.length < 2) { nav.closest('[data-mk-toc-wrap]')?.setAttribute('hidden', ''); return; }
    const used = new Set();
    heads.forEach((h) => {
        if (!h.id) {
            let id = slug(h.textContent) || 'section';
            while (used.has(id)) id += '-2';
            h.id = id;
        }
        used.add(h.id);
    });
    const links = heads.map((h) => Object.assign(document.createElement('a'), { href: `#${h.id}`, textContent: h.textContent.replace(/^\d+\.\s*/, '') }));
    nav.append(...links);
    $$('[data-mk-toc-copy]').forEach((other) => other.append(...links.map((a) => a.cloneNode(true))));
    const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
            if (!en.isIntersecting) return;
            links.forEach((a) => a.setAttribute('aria-current', String(a.hash === `#${en.target.id}`)));
            const label = $('[data-mk-toc-label]');
            if (label) label.textContent = en.target.textContent.replace(/^\d+\.\s*/, '');
        });
    }, { rootMargin: '-15% 0px -75% 0px' });
    heads.forEach((h) => io.observe(h));
});

/* ---------- copy ---------- */
$$('[data-mk-copy]').forEach((btn) => {
    const label = btn.innerHTML;
    btn.addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(btn.dataset.mkCopy);
            btn.textContent = 'Copied';
        } catch (e) {
            btn.textContent = 'Press ⌘C to copy';
        }
        setTimeout(() => { btn.innerHTML = label; }, 1600);
    });
});

/* "Show all" on a list without a filter bar */
$$('[data-mk-more]').forEach((btn) => {
    if ($(`[data-mk-filter="${btn.dataset.mkMore}"]`)) return;
    btn.addEventListener('click', () => {
        $$('[data-mk-extra]', document.getElementById(btn.dataset.mkMore)).forEach((row) => { row.hidden = false; });
        btn.parentElement.hidden = true;
    });
});

/* ---------- homepage hero: a shown line and a typed one, an arrow from each onto its carousel of song cards ---------- */
$$('[data-hh]').forEach((hero) => {
    const svg = $('.hh-arrows', hero);
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const phone = () => matchMedia('(max-width:1023px)').matches;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    const esc = (t) => t.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const drawn = new Set();

    // A ring of cards (the Bencho carousel): card i sits at angle (i - turn) * step, never wrapped, so the front card
    // is big and low and the back ones small and lifted. Drag or swipe to turn it (a quick flick carries on), the arrow
    // keys step it, and it settles on the nearest card with a quart-out glide. Clicking a card at the back brings it to
    // the front; clicking the front card opens it. The card under the pointer tilts toward it.
    const ring = (cz) => {
        const slots = $$('.hh-cz-slot', cz);
        const n = slots.length;
        const step = (Math.PI * 2) / n;
        const LEAN = 32, PULL = 140, TOSS = 150;
        let turn = 0, raf = 0, drag = null, dragged = false;
        const orbit = () => Math.min(138, (cz.clientWidth - 160) / 2 - 4);
        const paint = () => {
            const o = orbit();
            if (o <= 0) return;
            slots.forEach((el, i) => {
                const th = (i - turn) * step, f = (Math.cos(th) + 1) / 2;
                el.style.transform = `translate(-50%,-50%) translate(${(Math.sin(th) * o).toFixed(2)}px,${(-(1 - f) * LEAN + 8).toFixed(2)}px) rotate(var(--a)) scale(${(0.5 + 0.5 * f).toFixed(4)})`;
                el.style.zIndex = Math.round(f * 100);
            });
        };
        const glide = (to) => {
            cancelAnimationFrame(raf);
            if (reduce) { turn = to; paint(); return; }
            const from = turn, t0 = performance.now();
            const tick = (now) => {
                const p = Math.min(1, (now - t0) / 620);
                turn = from + (to - from) * (1 - (1 - p) ** 4);
                paint();
                if (p < 1) raf = requestAnimationFrame(tick);
            };
            raf = requestAnimationFrame(tick);
        };
        const front = () => ((Math.round(turn) % n) + n) % n;
        cz.addEventListener('pointerdown', (e) => {
            if (e.button) return;
            cancelAnimationFrame(raf);
            dragged = false;
            drag = { id: e.pointerId, x0: e.clientX, t0: turn, last: e.clientX, t: e.timeStamp, vx: 0, moved: false };
        });
        cz.addEventListener('pointermove', (e) => {
            if (!drag || e.pointerId !== drag.id) return;
            const dx = e.clientX - drag.x0;
            if (!drag.moved && Math.abs(dx) > 4) {
                drag.moved = true;
                cz.dataset.held = 'true';
                try { cz.setPointerCapture(e.pointerId); } catch (_) {}
            }
            if (!drag.moved) return;
            const dt = Math.max(1, e.timeStamp - drag.t);
            drag.vx = (drag.vx + (e.clientX - drag.last) / dt) / 2;
            drag.last = e.clientX;
            drag.t = e.timeStamp;
            turn = drag.t0 - dx / PULL;
            paint();
        });
        const up = () => {
            if (!drag) return;
            const { moved, vx } = drag;
            drag = null;
            cz.dataset.held = 'false';
            if (!moved) return;
            // The click that follows a drag must not open the card; the next press clears this.
            dragged = true;
            glide(Math.round(turn + Math.max(-2, Math.min(2, (-vx * TOSS) / PULL))));
        };
        cz.addEventListener('pointerup', up);
        cz.addEventListener('pointercancel', up);
        cz.addEventListener('click', (e) => {
            const slot = e.target.closest('.hh-cz-slot');
            if (dragged) { e.preventDefault(); return; }
            if (!slot) return;
            const i = slots.indexOf(slot);
            if (i !== front()) {
                e.preventDefault();
                const d = ((((i - Math.round(turn)) % n) + n + Math.floor(n / 2)) % n) - Math.floor(n / 2);
                glide(Math.round(turn) + d);
            }
        });
        cz.addEventListener('keydown', (e) => {
            const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
            if (!d) return;
            e.preventDefault();
            glide(Math.round(turn) + d);
        });
        $$('.hh-cz-card', cz).forEach((card) => {
            card.addEventListener('pointermove', (e) => {
                if (e.pointerType === 'touch' || drag?.moved || reduce) return;
                const b = card.getBoundingClientRect();
                const px = ((e.clientX - b.left) / b.width) * 2 - 1, py = ((e.clientY - b.top) / b.height) * 2 - 1;
                card.style.transform = `translateZ(-6px) rotateX(${(-py * 7).toFixed(2)}deg) rotateY(${(px * 7).toFixed(2)}deg)`;
            });
            card.addEventListener('pointerleave', () => { card.style.transform = ''; });
        });
        paint();
        new ResizeObserver(paint).observe(cz);

        return { paint, front, glide: (d) => glide(Math.round(turn) + d) };
    };
    const rings = $$('.hh-cz', hero).map((cz) => Object.assign(ring(cz), { cz }));

    // Phone: one carousel at a time.
    const show = (k) => {
        hero.dataset.show = String(k);
        $$('[data-hh-show]', hero).forEach((b) => b.setAttribute('aria-pressed', String(+b.dataset.hhShow === k)));
        rings.forEach((r) => r.paint());
        redraw();
    };
    hero.addEventListener('click', (e) => {
        const b = e.target.closest('[data-hh-show]');
        if (b) show(+b.dataset.hhShow);
    });

    // The arrows, measured from the real layout. Desktop: a short arrow from the end of each highlighted word to the
    // left edge of its carousel. Phone and tablet: only the second line has one, pointing down at the switch.
    const arrow = (k, grow) => {
        const g0 = svg && $(`g[data-a="${k}"]`, svg);
        const txt = $(`[data-hh-line="${k}"] .hh-txt`, hero);
        const mark = txt && $('.hh-mark', txt);
        let target = $(`[data-hh-t="${k}"]`, hero);
        if (phone()) target = k === 1 ? null : $('.hh-seg', hero) ?? target;
        if (!svg || !mark || !target || !target.getClientRects().length) { g0?.remove(); drawn.delete(k); return; }
        const H = hero.getBoundingClientRect();
        const R = (r) => ({ l: r.left - H.left, t: r.top - H.top, r: r.right - H.left, b: r.bottom - H.top });
        const cards = target.matches('.hh-cz') ? $$('.hh-cz-card', target).map((c) => R(c.getBoundingClientRect())) : [];
        const t = cards.length
            ? { l: Math.min(...cards.map((c) => c.l)), t: Math.min(...cards.map((c) => c.t)), r: Math.max(...cards.map((c) => c.r)), b: Math.max(...cards.map((c) => c.b)) }
            : R(target.getBoundingClientRect());
        const m = R(mark.getBoundingClientRect());
        let p0, p3, c1, c2;
        if (phone()) {
            p0 = [m.r + 8, (m.t + m.b) / 2];
            p3 = [Math.min(m.r + 24, t.r - 32), t.t - 8];
            c1 = [p0[0] + 28, p0[1]];
            c2 = [p3[0], p3[1] - 28];
        } else {
            p0 = [m.r + 12, (m.t + m.b) / 2];
            p3 = [t.l - 12, t.t + Math.min(56, (t.b - t.t) / 3)];
            const dx = p3[0] - p0[0];
            c1 = [p0[0] + dx * 0.55, p0[1]];
            c2 = [p3[0] - dx * 0.45, p3[1]];
        }
        const ang = Math.atan2(p3[1] - c2[1], p3[0] - c2[0]);
        const head = (a) => [p3[0] - 10 * Math.cos(ang + a), p3[1] - 10 * Math.sin(ang + a)];
        const f = (v) => v.map((x) => x.toFixed(1)).join(' ');
        let g = g0;
        if (!g) { g = document.createElementNS('http://www.w3.org/2000/svg', 'g'); g.dataset.a = k; svg.append(g); }
        g.innerHTML = `<path d="M${f(p0)} C${f(c1)} ${f(c2)} ${f(p3)}"/><path d="M${f(head(0.5))} L${f(p3)} L${f(head(-0.5))}"/>`;
        drawn.add(k);
        if (!grow) return;
        const [line, tip] = g.children;
        const L = line.getTotalLength();
        line.style.strokeDasharray = L;
        line.style.strokeDashoffset = L;
        tip.style.opacity = 0;
        line.getBoundingClientRect();
        line.style.transition = 'stroke-dashoffset .55s ease-in-out';
        line.style.strokeDashoffset = 0;
        setTimeout(() => { tip.style.transition = 'opacity .15s'; tip.style.opacity = 1; }, 500);
    };
    let rt;
    // Once the intro is over both arrows are recomputed (crossing the phone width adds or drops the first one);
    // during it, only the ones already drawn.
    function redraw() { clearTimeout(rt); rt = setTimeout(() => (hero.dataset.hhDone ? [1, 2] : [...drawn]).forEach((k) => arrow(k, false)), 60); }
    addEventListener('resize', redraw);
    document.fonts?.ready.then(redraw);

    // Types a line over the real (hidden) text. The overlay always holds the whole line, the untyped part invisible,
    // so it wraps exactly like the final line from the first letter: the lines, subheader and buttons never move. The
    // highlight appears once its word is fully typed; the caret takes no width.
    const type = async (k) => {
        const line = $(`[data-hh-line="${k}"]`, hero);
        const txt = $('.hh-txt', line);
        const word = $('.hh-mark', txt).textContent;
        const prefix = txt.textContent.slice(0, txt.textContent.length - word.length);
        const typed = document.createElement('span');
        typed.className = 'hh-typed';
        typed.setAttribute('aria-hidden', 'true');
        line.append(typed);
        const full = prefix + word;
        const ghost = (t) => (t ? `<span class="hh-ghost">${esc(t)}</span>` : '');
        const caret = '<span class="hh-caret"></span>';
        for (let i = 0; i <= full.length; i++) {
            const a = full.slice(0, Math.min(i, prefix.length));
            const b = i > prefix.length ? full.slice(prefix.length, i) : '';
            const inPrefix = i <= prefix.length;
            typed.innerHTML = esc(a) + (inPrefix ? caret : '') + ghost(prefix.slice(a.length))
                + `<span class="hh-w"><span class="hh-mark${b.length < word.length ? ' hh-pend' : ''}">${esc(b)}${inPrefix ? '' : caret}${ghost(word.slice(b.length))}</span></span>`;
            await wait(i < prefix.length ? 38 : 70);
        }
        typed.remove();
        line.classList.add('hh-on');
    };

    // Runs once the hero is on screen (a background tab has no layout to measure and doesn't animate). If that
    // takes more than 3 seconds the inline script has already shown everything, and the arrows are just drawn.
    const run = () => {
        const done = () => { hero.dataset.hhDone = '1'; [1, 2].forEach((k) => arrow(k, false)); };
        if (!hero.classList.contains('hh-anim')) { done(); return; }
        hero.dataset.hhStarted = '1';
        $$('.hh-g-l', hero).forEach((p) => p.style.setProperty('--len', Math.ceil(p.getTotalLength())));
        (async () => {
            for (const k of [1, 2]) {
                // The first line comes already shown (hh-on in the page), so it is the first big paint instead of a
                // late one at the end of its typing; only the second line types.
                if (!$(`[data-hh-line="${k}"]`, hero).classList.contains('hh-on')) await type(k);
                const cz = $(`[data-hh-t="${k}"]`, hero);
                if (!cz) continue;
                await wait(30);
                arrow(k, true);
                await wait(650);
                if (k === 1) $('.hh-seg', hero)?.classList.add('hh-in');
                else if (phone() && $('.hh-seg', hero)) show(2);
                cz.classList.add('hh-in');
                await wait(1300);
                // Measured again once the carousel has finished growing in (it was scaled down when first drawn).
                arrow(k, false);
            }
            hero.dataset.hhDone = '1';
        })().catch(() => { hero.classList.remove('hh-anim'); done(); });
    };
    if (!('IntersectionObserver' in window)) run();
    else {
        const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { io.disconnect(); run(); } });
        io.observe(hero);
    }
});

/* ---------- homepage hero: stories take turns, each card is wired to its dot, the curve has a tooltip ---------- */
$$('[data-hm-hero]').forEach((hero) => {
    const DUR = 16000;
    const tabs = $$('[data-hm-tab]', hero);
    const stories = $$('[data-hm-story]', hero);
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    hero.style.setProperty('--hm-dur', `${DUR / 1000}s`);

    const wire = (story) => {
        const stage = $('.hm-stage', story);
        const svg = $('.hm-wires', stage);
        if (!stage || !svg || story.hidden || getComputedStyle(svg).display === 'none') return;
        const R = stage.getBoundingClientRect();
        if (!R.width) return;
        svg.setAttribute('viewBox', `0 0 ${R.width} ${R.height}`);
        svg.innerHTML = $$('[data-hm-ev]', stage).map((card) => {
            const i = card.dataset.hmEv;
            const dot = $(`[data-hm-dot="${i}"]`, stage);
            if (!dot) return '';
            const a = card.getBoundingClientRect();
            const b = dot.getBoundingClientRect();
            const x1 = a.left + a.width / 2 - R.left, y1 = a.bottom - R.top;
            const x2 = b.left + b.width / 2 - R.left, y2 = b.top + b.height / 2 - R.top, m = (y1 + y2) / 2;
            return `<path class="hm-w${i === '0' ? ' hm-ws' : ''}" data-hm-wire="${i}" style="--i:${i}" d="M${x1.toFixed(1)},${y1.toFixed(1)} C${x1.toFixed(1)},${m.toFixed(1)} ${x2.toFixed(1)},${m.toFixed(1)} ${x2.toFixed(1)},${(y2 - 9).toFixed(1)}"/>`;
        }).join('');
        $$('path', svg).forEach((p) => { const L = p.getTotalLength(); p.style.strokeDasharray = L; p.style.setProperty('--L', L); });
    };
    const play = (story) => { story.classList.remove('hm-play'); void story.offsetWidth; wire(story); if (!reduce) story.classList.add('hm-play'); };
    let current = 0;
    const show = (i) => {
        current = i;
        tabs.forEach((t) => {
            const on = +t.dataset.hmTab === i;
            t.setAttribute('aria-selected', String(on));
            const bar = $('.hm-prog b', t);
            if (bar) { bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = ''; }
        });
        stories.forEach((s) => { s.hidden = +s.dataset.hmStory !== i; if (!s.hidden) play(s); });
    };
    let timer = null;
    const restart = () => {
        clearInterval(timer);
        if (reduce || tabs.length < 2) return;
        timer = setInterval(() => { if (!hero.matches(':hover')) show((current + 1) % stories.length); }, DUR);
    };
    hero.addEventListener('click', (e) => { const t = e.target.closest('[data-hm-tab]'); if (t) { show(+t.dataset.hmTab); restart(); } });
    // The page picks the story to show before the first paint (one at random on the homepage); start from it once
    // the section is on screen. Until then it shows as it is, with nothing waiting on an animation, so a page opened
    // in a background tab (where animations don't run and nothing has a size) never leaves the cards invisible.
    const start = () => { show(Math.max(0, stories.findIndex((s) => !s.hidden))); restart(); };
    if (stories.length) {
        if (!('IntersectionObserver' in window)) start();
        else {
            const io = new IntersectionObserver((entries) => {
                if (!entries.some((e) => e.isIntersecting)) return;
                io.disconnect();
                start();
            }, { threshold: 0.25 });
            io.observe(hero);
        }
    }
    let rt;
    const rewire = () => { clearTimeout(rt); rt = setTimeout(() => stories.filter((s) => !s.hidden).forEach(wire), 120); };
    addEventListener('resize', rewire);
    document.addEventListener('visibilitychange', rewire);
    document.fonts?.ready.then(() => stories.filter((s) => !s.hidden).forEach(wire));

    // Hovering or focusing a card lights it, its connector and its dot (the other connectors fade); hovering the curve
    // near a dot does the same for that dot's card, and shows the date, the day count and the videos.
    const key = (el) => el.dataset.hmEv ?? el.dataset.hmDot ?? el.dataset.hmWire;
    const hot = (stage, i) => {
        if (!stage) return;
        $$('[data-hm-ev],[data-hm-dot],[data-hm-wire]', stage).forEach((el) => el.classList.toggle('hm-hot', i !== null && key(el) === i));
        stage.classList.toggle('hm-has-hot', i !== null);
    };
    hero.addEventListener('mouseover', (e) => {
        const el = e.target.closest('[data-hm-ev],[data-hm-dot]');
        const stage = e.target.closest('.hm-stage');
        if (stage && !e.target.closest('[data-hm-chart]')) hot(stage, el ? key(el) : null);
    });
    hero.addEventListener('mouseleave', () => $$('.hm-stage', hero).forEach((st) => hot(st, null)));
    hero.addEventListener('focusin', (e) => { const el = e.target.closest('[data-hm-ev]'); if (el) hot(el.closest('.hm-stage'), key(el)); });
    hero.addEventListener('focusout', (e) => { const el = e.target.closest('[data-hm-ev]'); if (el && !el.contains(e.relatedTarget)) hot(el.closest('.hm-stage'), null); });
    const fmt = (n) => n >= 1e6 ? `${(n / 1e6).toFixed(2).replace(/\.?0+$/, '')}M` : n >= 1e4 ? `${Math.round(n / 1e3)}K` : Math.round(n).toLocaleString('en-GB');
    const day = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    $$('[data-hm-chart]', hero).forEach((chart) => {
        const points = JSON.parse(chart.dataset.points);
        const events = JSON.parse(chart.dataset.events);
        const t = (iso) => Date.parse(`${iso}T00:00:00Z`);
        const t0 = t(points[0][0]), span = Math.max(1, t(points[points.length - 1][0]) - t0);
        const max = Math.max(...points.map((p) => p[1])) * 1.04;
        const guide = $('.hm-guide', chart), tip = $('.hm-tip', guide), dotEl = $('i', guide);
        const stage = chart.closest('.hm-stage');
        chart.addEventListener('mousemove', (e) => {
            if (matchMedia('(max-width:760px)').matches) return;
            const r = chart.getBoundingClientRect();
            const at = t0 + Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * span;
            let k = points.findIndex((p) => t(p[0]) > at);
            k = k < 0 ? points.length - 1 : Math.max(0, k - 1);
            let point = points[k];
            const hit = events.findIndex((ev) => Math.abs(t(ev.date) - at) < span * 0.025);
            if (hit >= 0) point = points.filter((p) => p[0] <= events[hit].date).pop() ?? points[0];
            const x = (t(point[0]) - t0) / span;
            guide.hidden = false;
            guide.style.left = `${x * 100}%`;
            guide.classList.toggle('hm-flip', x > 0.7);
            dotEl.style.top = `${r.height - 6 - (point[1] / max) * (r.height - 16)}px`;
            const days = Math.round((t(point[0]) - t0) / 864e5);
            tip.innerHTML = `<b>${day(point[0])}</b><span>${days ? `Day ${days} after Tokchart found it` : 'The day Tokchart found it'}</span><span><em>${fmt(point[1])}</em> TikTok videos</span>${hit >= 0 ? `<span class="hm-tev"></span>` : ''}`;
            if (hit >= 0) $('.hm-tev', tip).textContent = events[hit].label;
            hot(stage, hit >= 0 ? String(hit) : null);
        });
        chart.addEventListener('mouseleave', () => { guide.hidden = true; hot(stage, null); });
    });
});

/* ---------- homepage About to blow up: the ranked-list card's actions, and the phone row's dots ---------- */
// The cards are livewire/ranked-list-v2/card.blade.php, which calls rankedTable's methods; here they get the few they
// use. Hiding shares the About to blow up page's list (the same key), so a card hidden here is hidden there too.
const BU_HIDDEN = 'rt-hidden-about-to-blow-up';
const readJson = (k) => { try { return JSON.parse(localStorage.getItem(k)) ?? {}; } catch (e) { return {}; } };
const toast = (detail) => window.dispatchEvent(new CustomEvent('ui-toast', { detail }));
document.addEventListener('alpine:init', () => {
    window.Alpine.data('hmCards', () => ({
        hidden: readJson(BU_HIDDEN),
        panel: null,
        rowClick(e) {
            if (e.target.closest('a,button')) return;
            const a = e.currentTarget.querySelector('a.rt-link[href]');
            if (a) window.location.href = a.href;
        },
        async copyLink(href) {
            try {
                await navigator.clipboard.writeText(new URL(href, window.location.origin).href);
                toast({ tone: 'ok', title: 'Link copied', text: 'The page, ready to paste.' });
            } catch (e) {
                toast({ tone: 'error', title: "Couldn't copy the link", text: 'Your browser blocked the clipboard.' });
            }
        },
        save() { try { localStorage.setItem(BU_HIDDEN, JSON.stringify(this.hidden)); } catch (e) {} },
        hide(id, title) {
            this.hidden = { ...this.hidden, [id]: true };
            this.save();
            toast({ tone: 'info', title: 'Hidden from this list', text: title, action: { label: 'Undo', run: () => { const { [id]: _, ...rest } = this.hidden; this.hidden = rest; this.save(); } } });
        },
    }));
});
$$('[data-hm-bu]').forEach((sec) => {
    const row = $('[data-hm-swipe]', sec);
    const dots = $$('.hm-dots i', sec);
    if (!row || !dots.length) return;
    row.addEventListener('scroll', () => {
        const cards = $$('.rt-card', row).filter((c) => c.offsetParent);
        const mid = row.scrollLeft + row.clientWidth / 2;
        let k = 0;
        cards.forEach((c, i) => { if (c.offsetLeft <= mid) k = i; });
        dots.forEach((d, i) => d.classList.toggle('hm-on', i === k));
    }, { passive: true });
});

/* ---------- homepage browser window around today's chart ---------- */
// The window keeps its own history of the chart's pages (the browser's would also step this page), so Back and Forward
// only ever move the chart. Its address shows the live page the chart is (/embed/… → /dashboard/…).
$$('[data-hm-br]').forEach((br) => {
    const frame = $('[data-hm-br-frame]', br);
    if (!frame) return;
    const path = $('[data-hm-br-path]', br);
    const btn = (k) => $(`[data-hm-br-act="${k}"]`, br);
    const opens = $$('[data-hm-br-open]', br.closest('section') ?? br);
    const stack = [];
    let at = -1;
    let moving = false;
    const live = (u) => u.pathname.replace(/^\/embed\/trending-sounds/, '/dashboard/tiktok-trending-sounds');
    const loaded = () => {
        let href;
        try { href = frame.contentWindow.location.href; } catch (e) { return; }
        if (!href || href === 'about:blank') return;
        if (!moving && stack[at] !== href) { stack.splice(at + 1); stack.push(href); at = stack.length - 1; }
        moving = false;
        const p = live(new URL(href));
        if (path) path.textContent = p;
        opens.forEach((a) => { a.href = p; });
        btn('back').disabled = at <= 0;
        btn('fwd').disabled = at >= stack.length - 1;
    };
    frame.addEventListener('load', loaded);
    if (frame.parentNode.classList.contains('hm-br-on')) loaded();
    // The chart only loads once the window is a quarter of a screen from view (a full screen still reaches it from the
    // top of the page on a laptop); until it has loaded its stand-in rows show.
    if (frame.dataset.src && !frame.getAttribute('src')) {
        const start = () => { if (!frame.getAttribute('src')) frame.src = frame.dataset.src; };
        if (!('IntersectionObserver' in window)) start();
        else {
            const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { io.disconnect(); start(); } }, { rootMargin: '25% 0px' });
            io.observe(br);
        }
    }
    const go = (i) => { if (i < 0 || i >= stack.length) return; at = i; moving = true; frame.contentWindow.location.replace(stack[i]); };
    btn('back')?.addEventListener('click', () => go(at - 1));
    btn('fwd')?.addEventListener('click', () => go(at + 1));
    btn('reload')?.addEventListener('click', () => { try { frame.contentWindow.location.reload(); } catch (e) { frame.src = frame.src; } });
});

/* ---------- homepage "found early" cards: a timeline row and its dot on the curve light each other ---------- */
$$('.hm-fc').forEach((card) => {
    const light = (k) => {
        $$('[data-hm-tl-dot]', card).forEach((d) => d.classList.toggle('hm-on', d.dataset.hmTlDot === k));
        $$('[data-hm-tl-row]', card).forEach((r) => r.classList.toggle('hm-on', r.dataset.hmTlRow === k));
    };
    card.addEventListener('mouseover', (e) => { const el = e.target.closest('[data-hm-tl-row],[data-hm-tl-dot]'); light(el ? (el.dataset.hmTlRow ?? el.dataset.hmTlDot) : null); });
    card.addEventListener('focusin', (e) => { const r = e.target.closest('[data-hm-tl-row]'); if (r) light(r.dataset.hmTlRow); });
    card.addEventListener('mouseleave', () => light(null));
});

/* ---------- homepage: today's chart window grows into place as it scrolls in ---------- */
// Port of the approved mockup (fxSize/fxTick). The window is laid out once at its full size (wider than the frame on
// desktop) and only its transform changes per frame, so the table in the iframe never re-lays out. p runs 0 → 1 from
// when its top is at 92% of the screen to 30%, eased (smoothstep). Until it has landed (p > .98) the chart's own page
// doesn't scroll, so a wheel or swipe over the window scrolls this page instead; clicks work at every size.
$$('[data-hm-fx]').forEach((fx) => {
    const sec = fx.closest('section') ?? fx;
    const frame = $('[data-hm-br-frame]', fx);
    const reduce = matchMedia('(prefers-reduced-motion: reduce)');
    let queued = 0;
    let live = null;
    const setLive = (on) => {
        if (on === live) return;
        try {
            const root = frame?.contentDocument?.documentElement;
            if (!root) return;
            root.style.overflow = on ? '' : 'hidden';
            live = on;
        } catch (e) {}
    };
    const tick = () => {
        queued = 0;
        if (reduce.matches) { sec.style.setProperty('--p', '1'); setLive(true); return; }
        const top = fx.getBoundingClientRect().top;
        const vh = innerHeight;
        const t = Math.min(1, Math.max(0, (vh * 0.92 - top) / (vh * 0.62)));
        const p = t * t * (3 - 2 * t);
        sec.style.setProperty('--p', p.toFixed(4));
        setLive(p > 0.98);
    };
    const size = () => {
        const vw = document.documentElement.clientWidth;
        const fw = fx.clientWidth;
        const phone = vw <= 732;
        const gw = phone ? fw : Math.min(vw - 32, 1440);
        fx.style.setProperty('--gw', `${gw}px`);
        fx.style.setProperty('--s0', ((fw * (phone ? 0.86 : 0.62)) / gw).toFixed(4));
        tick();
    };
    frame?.addEventListener('load', () => { live = null; tick(); });
    addEventListener('scroll', () => { if (!queued) queued = requestAnimationFrame(tick); }, { passive: true });
    addEventListener('resize', size);
    reduce.addEventListener?.('change', tick);
    size();
});
